import { useEffect, useState } from "react";
import { Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CodeIcon from "@mui/icons-material/Code";
import ReplayIcon from "@mui/icons-material/Replay";
import { ApiRequest } from "GlobalFunctions";
import { ProjectConfig } from "Global";
import { formatCurrency } from "./helpers/invoiceUtils";

const statusColors: any = { pending: "warning", sent: "info", accepted: "success", rejected: "error", error: "error" };
const statusLabels: any = { pending: "Pendiente", sent: "Enviado", accepted: "Aceptada", rejected: "Rechazada", error: "Error" };

export default function InvoiceViewer(props: any) {
  const { setLoading } = props;
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [doc, setDoc] = useState<any>(null);

  const loadDetail = () => {
    setLoading(true);
    ApiRequest({
      url: "/dian/invoice/detail",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query: { document_id: id },
      setResponse: (response: any) => {
        setDoc(response.document || null);
        setLoading(false);
      },
    });
  };

  useEffect(() => { loadDetail(); }, [id]);

  const retryNC = (docId: number) => {
    setLoading(true);
    ApiRequest({
      url: "/dian/document/retry",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: { document_id: docId },
      setResponse: (response: any) => {
        setLoading(false);
        if (response.success) {
          enqueueSnackbar("Nota Crédito reenviada correctamente", { variant: "success" });
          loadDetail();
        } else {
          enqueueSnackbar(response.error || "Error al reenviar", { variant: "error" });
        }
      },
    });
  };

  if (!doc) return null;

  const downloadPdf = () => {
    window.open(`${ProjectConfig.api_url}${doc.pdf_url}`, "_blank");
  };

  const downloadXml = () => {
    window.open(`${ProjectConfig.api_url}/dian/document/xml?document_id=${id}&ltkn=${localStorage.getItem("ltkn")}`, "_blank");
  };

  return (
    <div id="InvoiceViewer" className="ModuleContainer">
      <div className="ModuleWrapper">
        {/* HEADER */}
        <div className="boxContainerHeader">
          <div className="boxContainerTitle" style={{ display: "flex", alignItems: "center" }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/invoicing/facturas")}
              sx={{ mr: 2, borderRadius: 2, textTransform: "none" }}
            >
              Volver
            </Button>
            <h2 className="h2_title">Factura {doc.prefix}{doc.number}</h2>
          </div>
          <div className="boxContainerActions" style={{ display: "flex", gap: 8 }}>
            {doc.status === "accepted" && doc.pdf_path && (
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} color="error" onClick={downloadPdf} sx={{ borderRadius: 2, textTransform: "none" }}>
                PDF
              </Button>
            )}
            {doc.status === "accepted" && (
              <Button variant="outlined" startIcon={<CodeIcon />} onClick={downloadXml} sx={{ borderRadius: 2, textTransform: "none" }}>
                XML
              </Button>
            )}
          </div>
        </div>

        {/* STATUS & CUFE */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 3 }}>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <Chip
              label={statusLabels[doc.status] || doc.status}
              color={statusColors[doc.status] || "default"}
              sx={{ fontWeight: 500, borderRadius: 1.5 }}
            />
            <Typography variant="body2" color="text.secondary">
              Fecha: {doc.issue_date ? new Date(doc.issue_date).toLocaleDateString("es-CO") : "--"}
            </Typography>
          </div>
          {doc.cufe && (
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1.5, wordBreak: "break-all", fontSize: 12, fontFamily: "monospace" }}>
              CUFE: {doc.cufe}
            </Typography>
          )}
          {doc.validation_errors && (
            <Paper variant="outlined" sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "#fff3e0", borderColor: "#ffcc80" }}>
              <Typography variant="body2" color="error.main">{doc.validation_errors}</Typography>
            </Paper>
          )}
        </Paper>

        {/* CLIENT */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
            Cliente
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>{doc.buyer_name}</Typography>
          <Typography variant="body2" color="text.secondary">NIT/CC: {doc.buyer_nit}</Typography>
          {doc.buyer?.address && (
            <Typography variant="body2" color="text.secondary">{doc.buyer.address}</Typography>
          )}
        </Paper>

        {/* LINE ITEMS */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
          <div style={{ padding: "20px 24px 12px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
              Items
            </Typography>
          </div>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 }}>Descripcion</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 }}>Cant.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 }}>Precio Unit.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 }}>IVA</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(doc.lineItems || []).map((item: any, i: number) => (
                  <TableRow key={i} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.5, color: "text.secondary" }}>{i + 1}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{item.description}</TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>{item.quantity}</TableCell>
                    <TableCell align="right" sx={{ py: 1.5, fontFamily: "monospace" }}>{formatCurrency(item.price)}</TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>{item.tax_percent}%</TableCell>
                    <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, fontFamily: "monospace" }}>
                      {formatCurrency(item.quantity * item.price * (1 + item.tax_percent / 100))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* TOTALS */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 3 }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 32, flexWrap: "wrap" }}>
            <div>
              <Typography variant="body2" color="text.secondary" component="span">Subtotal: </Typography>
              <Typography variant="body2" fontWeight={600} fontFamily="monospace" component="span">{formatCurrency(doc.total_amount || 0)}</Typography>
            </div>
            <div>
              <Typography variant="body2" color="text.secondary" component="span">Impuestos: </Typography>
              <Typography variant="body2" fontWeight={600} fontFamily="monospace" component="span">{formatCurrency(doc.tax_amount || 0)}</Typography>
            </div>
            <div>
              <Typography variant="body1" color="text.secondary" component="span">Total: </Typography>
              <Typography variant="body1" fontWeight={700} fontFamily="monospace" color="primary.main" component="span">
                {formatCurrency(doc.payable_amount || 0)}
              </Typography>
            </div>
          </div>
          {doc.paymentMeans?.code && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: "right" }}>
              Medio de pago: {doc.paymentMeans.code === "1" || doc.paymentMeans.code === "10" ? "Contado" : "Credito"}
              {doc.paymentMeans.dueDate ? ` -- Vence: ${doc.paymentMeans.dueDate}` : ""}
            </Typography>
          )}
          {doc.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "right", fontStyle: "italic" }}>
              Notas: {doc.notes}
            </Typography>
          )}
        </Paper>

        {/* CREDIT NOTES */}
        {doc.creditNotes?.length > 0 && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px 12px" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
                Notas Crédito ({doc.creditNotes.length})
              </Typography>
            </div>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13 }}>Número</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13 }}>Estado</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13 }}>Monto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {doc.creditNotes.map((cn: any) => (
                    <TableRow key={cn.document_id}>
                      <TableCell sx={{ fontWeight: 600 }}>{cn.prefix}{cn.number}</TableCell>
                      <TableCell>{cn.issue_date ? new Date(cn.issue_date).toLocaleDateString("es-CO") : "--"}</TableCell>
                      <TableCell>
                        <Chip label={statusLabels[cn.status] || cn.status} color={statusColors[cn.status] || "default"} size="small" sx={{ fontWeight: 500, fontSize: 12, borderRadius: 1.5 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontFamily: "monospace" }}>{formatCurrency(cn.payable_amount || 0)}</TableCell>
                      <TableCell align="right" sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        {(cn.status === "error" || cn.status === "rejected") && (
                          <Button size="small" startIcon={<ReplayIcon />} color="warning"
                            onClick={() => retryNC(cn.document_id)}
                            sx={{ textTransform: "none", fontSize: 12 }}>
                            Reintentar
                          </Button>
                        )}
                        {cn.pdf_url && (
                          <Button size="small" startIcon={<PictureAsPdfIcon />} color="error"
                            onClick={() => window.open(`${ProjectConfig.api_url}${cn.pdf_url}`, "_blank")}
                            sx={{ textTransform: "none", fontSize: 12 }}>
                            PDF
                          </Button>
                        )}
                        <Button size="small" startIcon={<CodeIcon />}
                          onClick={() => window.open(`${ProjectConfig.api_url}/dian/document/xml?document_id=${cn.document_id}&ltkn=${localStorage.getItem("ltkn")}`, "_blank")}
                          sx={{ textTransform: "none", fontSize: 12 }}>
                          XML
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </div>
    </div>
  );
}
